import { OfferCardLogHistoryButton } from "./offer-card-log-history-button";
import { StopNotesTable } from "./offer-card-locations-stop-comments";
import { useDatesLocation } from "../hooks";
import { useTranslation } from "react-i18next";
import { useUTCDate } from "../../../../../shared/infrastructure/ui/hooks/use-utc-date";
import {
  CommentsIcon,
  Modal,
  useDisclosure,
} from "../../../../../shared/infrastructure";
import { OfferT, StopNotes, StopT } from "../../../domain";
import React, { useMemo } from "react";

type StopsProps = {
  stops: Array<StopT>;
};

const Stops = ({ stops }: StopsProps): React.ReactElement => {
  const [t] = useTranslation("global");
  const { dateFromUTC } = useUTCDate();

  return (
    <section className="absolute top-full left-[50%] z-[500] hidden translate-x-[-50%] rounded-[5px] border-[1px] border-solid border-[#D6D6D6] bg-[#FFFFFF] shadow-[2px_2px_2px_rgba(0,0,0,0.15)] group-hover/stops:block">
      {stops
        .map((stop, key) => (
          <section
            key={key}
            className="grid grid-cols-[20px_1fr_20px] items-center gap-[8px] p-[8px]"
          >
            <span className="text-center text-[10px] text-[#484848]">
              {key + 1}
            </span>
            <section className="grid grid-cols-[1fr_auto] gap-[4px_8px]">
              <span className="whitespace-nowrap text-left text-[10px] text-[#484848]">
                {stop.city}, {stop.state_code}
              </span>
              <span className="whitespace-nowrap text-left text-[10px] text-[#919191]">
                {dateFromUTC(stop.arrive_early_time).format("MM/DD/YYYY")}
              </span>
              <span className="whitespace-nowrap text-left text-[10px] text-[#919191]">
                {t<string>(
                  "context.private.components.offer-card-location.zip-code"
                )}
                : {stop.zip_code}
              </span>
              <span className="whitespace-nowrap text-left text-[10px] text-[#919191]">
                {dateFromUTC(stop.arrive_early_time).format("HH:mm")}
              </span>
            </section>
            <section className="grid h-[20px] w-[20px] place-items-center rounded-full bg-[#D6D6D6] text-center text-[10px] text-[#919191]">
              {stop.stop_type}
            </section>
          </section>
        ))
        .reduce(
          (prev, stop, key) => [
            ...prev,
            stop,
            ...(key === stops.length - 1
              ? []
              : [
                  <span
                    key={`${key}-${key}`}
                    className="ml-[36px] block h-[1px] w-[calc(100%-44px)] border-b-[1px] border-solid border-[#D6D6D6]"
                  />,
                ]),
          ],
          [] as Array<React.ReactElement>
        )}
    </section>
  );
};

type LocationProps = {
  stateCode: string;
  city: string;
  datetimeEarly: string;
  datetimeLate?: string;
  zipCode?: string;
  driverLoadUnload?: boolean;
  comments?: Array<StopNotes>;
};

const Location = ({
  stateCode,
  city,
  datetimeEarly,
  datetimeLate,
  // zipCode,
  driverLoadUnload,
  comments,
}: LocationProps): React.ReactElement => {
  const { open, isOpen, close } = useDisclosure();

  return (
    <section
      className={[
        "flex flex-col items-center justify-center gap-y-1 text-sm",
        "md:grid md:max-w-none md:grid-flow-col md:place-content-center md:place-items-center md:gap-[10px] md:gap-y-0",
      ].join(" ")}
    >
      <h1 className="relative inline-block text-[32px] font-semibold md:text-[34px] xl:text-[32px]">
        {stateCode}

        <section className="absolute -top-2 left-0 grid grid-cols-[12px_1fr_auto] items-center gap-[4px]">
          <button onClick={open}>
            <CommentsIcon width={12} height={12} fill="black" />
          </button>
          <span className="overflow-hidden text-ellipsis whitespace-pre-wrap text-[10px] font-[700] text-[#484848]">
            {comments?.length ?? 0}
          </span>
        </section>
      </h1>
      <section>
        <p className="line-clamp-2 xl:line-clamp-3 text-center text-sm capitalize leading-[18px] tracking-tighter  text-[#484848] md:text-start md:text-[14px] xl:text-sm">
          {city}
        </p>

        <div className="flex flex-col">
          <div className="flex gap-x-[2px]">
            <p className="w-max text-center text-[10px] leading-normal text-[#919191] md:text-start md:text-sm xl:text-[10px]">
              {datetimeEarly}
            </p>
            {datetimeLate ? (
              <p className="w-max text-[10px] text-[#919191] md:w-auto">
                {datetimeLate}
              </p>
            ) : null}
          </div>
          {/** zipCode ? (
            <span className="text-xs text-[#888889]">Zip code: {zipCode}</span>
          ) : null */}

          <span className="text-xs text-[#888889]">
            Driver Unload: {driverLoadUnload ? "Y" : "N"}
          </span>
        </div>
      </section>
      <Modal
        open={isOpen}
        onClose={close}
        content={<StopNotesTable stopNotes={comments || []} />}
        classNames={{
          content:
            "overflow-x-scroll overflow-y-hidden p-[20px] min-w-[1100px] max-h-[420px]",
          paper: "rounded-[5px] max-w-[85%]",
        }}
      />
    </section>
  );
};

type OfferCardLocationsProps = {
  offer: OfferT;
  showLogHistory?: boolean;
};

export const OfferCardLocations = ({
  offer,
  showLogHistory = false,
}: OfferCardLocationsProps): React.ReactElement => {
  const [t] = useTranslation("global");
  const origin = useDatesLocation(
    offer.origin.arrive_early_datetime,
    offer.origin.arrive_late_datetime
  );
  const destination = useDatesLocation(
    offer.destination.arrive_early_datetime,
    offer.destination.arrive_late_datetime
  );

  const dots = useMemo<React.ReactNode>(
    () =>
      new Array((offer.stops ?? []).length)
        .fill(0)
        .map((_, key) => (
          <span key={key} className="h-2 w-2 rounded-full bg-blue-400" />
        )),
    [offer.stops]
  );

  return (
    <section className="mx-auto flex max-w-[264px] justify-center sm:max-w-[443px] sm:items-center sm:justify-start md:mx-0 md:max-w-none md:items-center md:justify-center xl:grid xl:grid-cols-[1fr_110px_1fr_26px] xl:gap-[20px]">
      <div className="mr-4 w-20 md:w-full">
        <Location
          stateCode={offer.origin.state_code || offer.origin.state_id}
          city={offer.origin.city.toLowerCase()}
          datetimeEarly={origin[0]}
          datetimeLate={origin[1]}
          zipCode={offer.origin.zip_code}
          driverLoadUnload={offer.origin.driver_load_unload}
          comments={offer.origin.stop_notes}
        />
      </div>

      <section className="md:min-w-none grid h-fit w-full min-w-[70px] items-center place-self-center text-center md:h-auto md:max-w-[70px] xl:max-w-none xl:place-self-auto">
        <span className="text-sm text-[#484848]">
          {offer.distance ? offer.distance.toLocaleString("en-US") : 0} mi
        </span>
        <section className="grid grid-cols-[10px_1fr_10px] items-center">
          <section className="h-[10px] w-[10px] rounded-full border-[0.5px] border-solid border-[#9E9E9E] bg-black bg-clip-content p-[2px]">
            <span />
          </section>

          <section className="relative h-[0.5px] w-full bg-[#9E9E9E]">
            <section className="absolute top-[-3px] left-0 grid h-full w-full grid-flow-col justify-evenly">
              {dots}
            </section>
          </section>

          <section className="h-[10px] w-[10px] rounded-full border-[0.5px] border-solid border-[#9E9E9E] bg-black bg-clip-content p-[2px]">
            <span />
          </section>
        </section>
        {offer.stops && (
          <section className="group/stops relative cursor-pointer">
            <p className="text-sm text-[#3C7CF3]">
              {offer.stops.length}{" "}
              {offer.stops.length === 1
                ? t<string>(
                    "context.private.components.offer-card-location.stop"
                  )
                : t<string>(
                    "context.private.components.offer-card-location.stops"
                  )}
            </p>
            <Stops stops={offer.stops} />
          </section>
        )}
      </section>

      <div className="ml-4 w-20 md:w-full">
        <Location
          stateCode={offer.destination.state_code || offer.destination.state_id}
          city={offer.destination.city.toLowerCase()}
          datetimeEarly={destination[0]}
          datetimeLate={destination[1]}
          zipCode={offer.destination.zip_code}
          driverLoadUnload={offer.destination.driver_load_unload}
          comments={offer.destination.stop_notes}
        />
      </div>

      {showLogHistory && (
        <div className="hidden md:ml-4 md:flex xl:ml-0">
          <OfferCardLogHistoryButton offer={offer} />
        </div>
      )}
    </section>
  );
};
